import {
  Banner,
  ButtonPrimary,
  ButtonTertiary,
  IconExternalLink,
  IconUserActive,
  Type,
} from '@spotify-internal/encore-web';
import { GetErrorReportResponse } from 'app/ErrorReporting/types';
import BackButton from 'components/BackButton';
import { EntityAttribute, EntityAttributeRow, EntityHeader } from 'components/EntityHeader';
import { History } from 'history';
import { getSongwriterPageLink } from 'libs/utils/externalUrls';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useT } from '@spotify-internal/i18n-core';
import { formatDate } from '../../../libs/utils/moments';
import moment from 'moment';

const ActionHolder = styled.div`
  text-align: right;
`;

const StyledHeader = styled(Type.h1)`
  margin-block-end: 16px;
`;

const InfoBoxIcon = styled(IconUserActive)`
  width: 16px;
  margin-inline-end: 12px;
  padding-block-start: 8px;
`;

type Props = {
  history: History;
  errorReport: GetErrorReportResponse;
  onMarkReportResolved: () => Promise<void>;
};

const Header = ({ history, errorReport, onMarkReportResolved }: Props) => {
  const t = useT();
  const { songwriterName, creatorUri, message, reporterName, createdAt } = errorReport;
  const allIncorrectRecordingsResolved =
    !errorReport.incorrectRecordings ||
    errorReport.incorrectRecordings.filter((ir) => !ir.resolved).length === 0;

  const allMissingRecordingsResolved =
    !errorReport.missingRecordings ||
    errorReport.missingRecordings.filter((ir) => !ir.resolved).length === 0;

  // Track out own state, so we can provide immediate feedback to the user when they click the button
  const [disableMarkAsResolved, setDisableMarkAsResolved] = useState(
    allIncorrectRecordingsResolved && allMissingRecordingsResolved,
  );

  return (
    <React.Fragment>
      <BackButton
        text={t('BUTTON_ERRORREPORTHEADER_0', 'Error Reports', 'Back button text')}
        onClick={() => history.push('/manage-data')}
      />
      <EntityHeader
        headerMargin="24px"
        renderTitle={() => (
          <StyledHeader variant="alto">
            {t(
              'H_ERRORREPORTHEADER_0',
              'Error report for {songwriterName}',
              'Header for error report',
              { songwriterName },
            )}
          </StyledHeader>
        )}
        attributes={
          <EntityAttributeRow>
            <EntityAttribute
              label={t('L_ERRORREPORTHEADER_0', 'date', 'Label for list of error reports')}
              value={createdAt && formatDate(moment(createdAt))}
              hasPriority
            />
            <EntityAttribute
              label={t('L_ERRORREPORTHEADER_1', 'reported by', 'Label for list of error reports')}
              value={reporterName}
            />
          </EntityAttributeRow>
        }
        actions={
          <ActionHolder>
            <ButtonPrimary
              onClick={async () => {
                setDisableMarkAsResolved(true);
                await onMarkReportResolved();
              }}
              buttonSize="sm"
              disabled={disableMarkAsResolved}
            >
              {t(
                'BUTTON_ERRORREPORTHEADER_1',
                'Mark report as resolved',
                'Button to mark an error report as resolved',
              )}
            </ButtonPrimary>
            <br />
            <ButtonTertiary
              condensed
              semanticColor="textBrightAccent"
              iconTrailing={IconExternalLink}
              target="_blank"
              rel="noopener noreferrer"
              buttonSize="sm"
              href={getSongwriterPageLink(creatorUri)}
            >
              {t(
                'BUTTON_ERRORREPORTHEADER_2',
                'Open public page',
                'Button to open the public songwriter page',
              )}
            </ButtonTertiary>
          </ActionHolder>
        }
      />
      {message && (
        <Banner renderIcon={() => <InfoBoxIcon />}>
          <Type paddingBottom="8px" as="h2" variant="ballad">
            {t(
              'H_ERRORREPORTHEADER_1',
              'Message from {songwriterName} team',
              'Header of message from a songwriter team',
              { songwriterName },
            )}
          </Type>
          <Type as="p" variant="mesto">
            {message}
          </Type>
        </Banner>
      )}
    </React.Fragment>
  );
};

export default Header;
